import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    cancelMaintenanceDaily,
    deleteMaintenanceDaily,
    getMaintenanceDailyList,
} from '../../services/MaintenanceServices';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';

function DailyPlantLogTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromSalesSettings(salesSettings),
        [salesSettings],
    );

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || [])
                .filter((p) => p.status === true || p.status === 1 || p.status === 'Active')
                .map((p) => ({ value: p.id, label: p.plant_alias })));
            setIsInitialized(true);
        });
    }, [cookies]);

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, []),
        [salesSettings, plants],
    );

    const fetchRecords = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const [rangeFrom, rangeTo] = activeDateRange;
        if (!plantId || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }
        setIsLoading(true);
        return getMaintenanceDailyList(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
        })
            .then((response) => {
                setData({
                    records: (response.maintenance_daily_list || []).map((row) => {
                        const isActive = row.status === true || row.status === 1 || row.status === 'Active';
                        return {
                            ...row,
                            status_label: isActive ? 'Active' : 'Cancelled',
                            hide_row_edit: isActive ? 'No' : 'Yes',
                            hide_row_delete: 'No',
                        };
                    }),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load daily plant logs.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, plants, salesSettings]);

    useEffect(() => {
        if (isInitialized) fetchRecords();
    }, [fetchRecords, isInitialized]);

    const columns = useMemo(() => ([
        { fieldName: 'log_date', headerName: 'Date' },
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'production_qty', headerName: 'Production m3', align: 'right' },
        { fieldName: 'eb_units_kwh', headerName: 'EB kWh', align: 'right' },
        { fieldName: 'pump_hours', headerName: 'Pump Hrs', align: 'right' },
        { fieldName: 'prepared_by', headerName: 'Prepared By' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ]), []);

    const handleEdit = (id) => navigate(`/DailyPlantLogEdit/${id}`);

    const handleDelete = (id) => {
        const row = data.records.find((r) => String(r.id) === String(id));
        const isActive = row?.status_label === 'Active';
        Swal.fire({
            title: isActive ? 'Cancel this daily log?' : 'Delete this daily log?',
            icon: 'warning',
            showCancelButton: true,
        }).then((result) => {
            if (!result.isConfirmed) return;
            const action = isActive
                ? cancelMaintenanceDaily(cookies, id)
                : deleteMaintenanceDaily(cookies, id);
            action
                .then(() => fetchRecords())
                .catch((error) => displayError(error?.response?.data || error, isActive ? 'Cancel Failed' : 'Delete Failed'));
        });
    };

    const handleExportCSV = () => {
        exportAsExcel(columns, data.records, 'daily_plant_log');
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="MAINTENANCE"
                    title="Daily Plant Log"
                    subtitle={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onAdd={() => navigate('/DailyPlantLog')}
                    onRefresh={fetchRecords}
                    onExportCSV={handleExportCSV}
                    isLoading={isLoading}
                >
                    <ReactTableWrapper
                        data={data.records}
                        columns={columns}
                        isLoading={isLoading}
                        onRowEdit={handleEdit}
                        onRowDelete={handleDelete}
                    />
                </ListPageLayout>
                <SalesListSettingsModal
                    show={showSettings}
                    onClose={() => setShowSettings(false)}
                    settings={salesSettings}
                    plants={plants}
                    onSave={(nextSettings) => {
                        saveSalesListSettings(cookies.myUserId, nextSettings);
                        setSalesSettings(nextSettings);
                        setShowSettings(false);
                    }}
                />
            </div>
        </div>
    );
}

export default DailyPlantLogTable;
